import React, { useState, useEffect, Fragment, useMemo, useRef, useContext } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NumberInput from './NumberInput';
import TextInput from './TextInput';
import Terminal from './Terminal';
import Switch from './Switch';
import PushButton from './PushButton';
import Gauge from './Gauge';
import LiquidWidget from './LiquidWidget';
import RadialGauge from './RadialGauge';
import WaterTankWidget from './WaterTankWidget';
import Slider from './Slider';
import Label from './Label';
import Chart from './Chart';
import PeriodColumnChart from './PeriodColumnChart';
import LED from './LED';
import TextTitle from './TextTitle';
import BackgroundImage from './BackgroundImage';
import BoxWidget from './BoxWidget';
import Menu from './Menu';
import './widget.css';
import Keypad from './Keypad';
import SegmentSwitch from './SegmentSwitch';
import ImageButton from './ImageButton';
import TableWidget from './Table';
import Fan from './Fan';
import WaterPipe from './WaterPipe';
import WaterElbow from './waterElbow';
import WaterPump from './WaterPump';
import TimeRange from './TimeRange';
import EnvironmentalStatus from './EnvironmentalStatus';
import { WidgetDeleteIcon, WidgetDuplicateIcon, WidgetSettingsIcon } from '../svg/TemplateIcons';
import { useAppTheme } from '../context/ThemeContext';
import { LanguageContext } from '../context/LanguageContext';
import { resolveThemeColors } from '../utils/themeColors';
import { normalizeWebDashboardLayoutMode, WEB_DASHBOARD_LAYOUT_MODES } from '../utils/webDashboardLayout';
import { getResponsiveWidgetVars } from '../utils/widgetResponsive';
  
const cloneWidgetValue = (value) => {
  if (value instanceof Blob) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(cloneWidgetValue);
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, nestedValue]) => {
      acc[key] = cloneWidgetValue(nestedValue);
      return acc;
    }, {});
  }

  return value;
};

const hasConfiguredColorValue = (props, keys) =>
  keys.some((key) => {
    const value = props?.[key];
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    return value !== undefined && value !== null;
  });

const getDatastreamColorFallback = (widgetType, props, datastreamMeta) => {
  const datastreamColor = String(datastreamMeta?.color || '').trim();
  if (!datastreamColor) return null;

  if (widgetType === 'label') {
    return hasConfiguredColorValue(props, ['color', 'colorLight']) ? null : datastreamColor;
  }

  if (
    widgetType === 'gauge' ||
    widgetType === 'radial-gauge' ||
    widgetType === 'liquid' ||
    widgetType === 'water-tank'
  ) {
    return hasConfiguredColorValue(props, [
      'color',
      'colorLight',
      'startColor',
      'startColorLight',
      'stopColor',
      'stopColorLight',
    ]) ? null : datastreamColor;
  }

  return null;
};

const hasMeaningfulDatastreamValue = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
};

const getDatastreamDisplayValue = (datastreamMeta, currentValue) => {
  if (hasMeaningfulDatastreamValue(datastreamMeta?.value)) {
    return datastreamMeta.value;
  }

  if (hasMeaningfulDatastreamValue(datastreamMeta?.defaultValue)) {
    return datastreamMeta.defaultValue;
  }

  return currentValue;
};

const FACTORED_WIDGET_TYPES = new Set(['gauge', 'radial-gauge', 'label']);
const DEFAULT_WIDGET_LABEL_FONT_SIZE = 15;
const DEFAULT_WIDGET_VALUE_FONT_SIZE = 30;

const getConfiguredFactor = (props, datastreamMeta) => {
  const factorCandidates = [
    props?.factor,
    props?.Factor,
    props?.multiplyFactor,
    props?.valueFactor,
    datastreamMeta?.factor,
    datastreamMeta?.Factor,
    datastreamMeta?.multiplyFactor,
    datastreamMeta?.valueFactor,
  ];

  for (const candidate of factorCandidates) {
    if (candidate === '' || candidate === null || candidate === undefined) continue;
    const numericFactor = Number(candidate);
    if (Number.isFinite(numericFactor)) {
      return numericFactor;
    }
  }

  return 1;
};

const multiplyNumericValue = (value, factor) => {
  if (factor === 1 || value === '' || value === null || value === undefined) {
    return value;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue * factor : value;
};

const applyDatastreamFactorToProps = (widgetType, props, datastreamMeta) => {
  if (!FACTORED_WIDGET_TYPES.has(widgetType)) {
    return props;
  }

  const factor = getConfiguredFactor(props, datastreamMeta);
  if (!Number.isFinite(factor) || factor === 1) {
    return {
      ...props,
      factor: props?.factor ?? 1,
    };
  }

  return {
    ...props,
    factor,
    min: multiplyNumericValue(props.min, factor),
    max: multiplyNumericValue(props.max, factor),
    value: multiplyNumericValue(props.value, factor),
  };
};

const toFiniteNumberOrNull = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const normalizeNumericBounds = (min, max) => {
  const minNum = toFiniteNumberOrNull(min);
  const maxNum = toFiniteNumberOrNull(max);

  if (minNum !== null && maxNum !== null && maxNum < minNum) {
    return { min: maxNum, max: minNum };
  }

  return { min: minNum, max: maxNum };
};

const clampValueToBounds = (value, min, max) => {
  if (value === '' || value === null || value === undefined) return value;

  const numericValue = Number(value);
  const bounds = normalizeNumericBounds(min, max);
  if (!Number.isFinite(numericValue)) {
    const raw = typeof value === 'string' ? value.trim() : '';
    const looksNumeric = raw !== '' && /^[-+]?(\d+(\.\d*)?|\.\d+)(e[-+]?\d+)?$/i.test(raw);

    if (!looksNumeric) {
      return value;
    }

    if (raw.startsWith('-') && bounds.min !== null) {
      return bounds.min;
    }

    if (!raw.startsWith('-') && bounds.max !== null) {
      return bounds.max;
    }

    if (bounds.min !== null && bounds.max !== null && bounds.min === bounds.max) {
      return bounds.min;
    }

    return value;
  }

  let nextValue = numericValue;

  if (bounds.min !== null && nextValue < bounds.min) {
    nextValue = bounds.min;
  }

  if (bounds.max !== null && nextValue > bounds.max) {
    nextValue = bounds.max;
  }

  return nextValue;
};

let globalResizeRaf = null;
const scheduleGlobalResize = () => {
  if (typeof window === 'undefined') return;
  if (globalResizeRaf !== null) return;

  globalResizeRaf = window.requestAnimationFrame(() => {
    globalResizeRaf = null;
    try {
      window.dispatchEvent(new Event('resize'));
    } catch (e) {
    }
  });
};

const getFontSizeMultiplier = () => {
  if (typeof window === 'undefined') return 1;
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--font-size-multiplier');
  const parsed = parseFloat(raw || '1');
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const measureTextWidth = (text, fontSizePx, fontWeight = 700) => {
  const content = String(text || '');
  if (!content) return 0;

  if (typeof document === 'undefined') {
    return content.length * fontSizePx * 0.62;
  }

  if (!measureTextWidth.canvas) {
    measureTextWidth.canvas = document.createElement('canvas');
  }

  const ctx = measureTextWidth.canvas.getContext('2d');
  if (!ctx) {
    return content.length * fontSizePx * 0.62;
  }

  ctx.font = `${fontWeight} ${fontSizePx}px Roboto, sans-serif`;
  return ctx.measureText(content).width;
};

const clampNumber = (value, min, max) => Math.min(Math.max(value, min), max);

const toWidgetFontSizeCssValue = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return null;
  return `${Math.min(Math.round(numericValue), 160)}px`;
};

const formatGaugeDisplayValue = ({ value, unit, showUnit, decimals, separator = '' }) => {
  let valueText = '—';
  const numericValue = Number(value);
  const numericDecimals = Number(decimals);

  if (value !== undefined && value !== null && value !== '') {
    if (Number.isFinite(numericValue)) {
      valueText = Number.isFinite(numericDecimals)
        ? parseFloat(numericValue.toFixed(Math.max(0, numericDecimals))).toString()
        : numericValue.toString();
    } else {
      valueText = String(value);
    }
  }

  const unitText = showUnit && String(unit || '').trim() ? String(unit).trim() : '';
  return unitText ? `${valueText}${separator}${unitText}` : valueText;
};


const Widget = ({
  id,
  type,
  title,
  transparentBackground,
  dataId,
  dataPin,
  decimals,
  position,
  width,
  height,
  top,
  left,
  renderRect,
  resize,
  resizeWidget,
  duplicate,
  remove,
  extraProps,
  settingsExtraProps,
  draggable,
  setEdit,
  did,
  tid,
  onTerminalCleared,
  setDeviceDatastream,
  deviceId,
  devices,
  datastreams,
  layoutMode: rawLayoutMode,
  onWidgetPropsChange,
  selected = false,
  dragOffset = null,
}) => {
  const { mode } = useAppTheme();
  const { lang } = useContext(LanguageContext);
  const isRtl = ['ar', 'ur'].includes(lang);
  const layoutMode = normalizeWebDashboardLayoutMode(rawLayoutMode);
  const isPercentageLayout = layoutMode === WEB_DASHBOARD_LAYOUT_MODES.PERCENTAGE;
  const isBaseWidget = String(id || '').startsWith('base-');

  const toPxNumber = (v, fallback = 0) => {
  if (v === null || v === undefined) return fallback;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const n = Number(String(v).replace('px', '').trim());
  return Number.isFinite(n) ? n : fallback;
};

const toCssLength = (value, fallback) => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'number' && Number.isFinite(value)) return `${value}%`;
  const stringValue = String(value).trim();
  if (!stringValue) return fallback;
  return stringValue.endsWith('%') ? stringValue : `${stringValue}%`;
};

const toLengthNumber = (value, fallback = 0) => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const normalized = String(value).replace('%', '').replace('px', '').trim();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const renderWidthPx = Number.isFinite(Number(renderRect?.width)) ? Number(renderRect.width) : null;
const renderHeightPx = Number.isFinite(Number(renderRect?.height)) ? Number(renderRect.height) : null;
const renderTopPx = Number.isFinite(Number(renderRect?.top)) ? Number(renderRect.top) : null;
const renderLeftPx = Number.isFinite(Number(renderRect?.left)) ? Number(renderRect.left) : null;
const useRenderRect =
  renderWidthPx !== null &&
  renderHeightPx !== null &&
  renderTopPx !== null &&
  renderLeftPx !== null;

const [dims, setDims] = useState(() => ({
  width: isPercentageLayout ? (useRenderRect ? renderWidthPx : 0) : toPxNumber(width, 0),
  height: isPercentageLayout ? (useRenderRect ? renderHeightPx : 0) : toPxNumber(height, 0),
}));
const [isCompactActionsMenuOpen, setIsCompactActionsMenuOpen] = useState(false);
const [isMobileViewport, setIsMobileViewport] = useState(() => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  return window.matchMedia('(max-width: 767px)').matches;
});
const compactActionsMenuRef = useRef(null);
const usesOverflowActionsMenu = resize && isMobileViewport;

useEffect(() => {
  if (isPercentageLayout) return;
  const nextW = toPxNumber(width, 0);
  const nextH = toPxNumber(height, 0);
  setDims((prev) => {
    if (Math.abs(nextW - prev.width) > 0.5 || Math.abs(nextH - prev.height) > 0.5) {
      return { width: nextW, height: nextH };
    }
    return prev;
  });
}, [width, height, isPercentageLayout]);

useEffect(() => {
  if (!isPercentageLayout || !useRenderRect) return;
  setDims((prev) => {
    if (
      Math.abs(renderWidthPx - prev.width) > 0.5 ||
      Math.abs(renderHeightPx - prev.height) > 0.5
    ) {
      return { width: renderWidthPx, height: renderHeightPx };
    }
    return prev;
  });
}, [isPercentageLayout, useRenderRect, renderWidthPx, renderHeightPx]);
useEffect(() => {
    if (!resize && !isBaseWidget && !isPercentageLayout) return;

    const element = document.getElementById(id);
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setDims(prev => {
          if (Math.abs(width - prev.width) > 1 || Math.abs(height - prev.height) > 1) {
            return { width, height };
          }
          return prev;
        });
      }
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [id, resize, isPercentageLayout, isBaseWidget]);

useEffect(() => {
  const needsResizeSignal = type === 'chart' || type === 'table' || type === 'period-column';
  if (!needsResizeSignal || isBaseWidget) return;

  scheduleGlobalResize();
}, [dims.width, dims.height, id, type, isBaseWidget]);

useEffect(() => {
  if (!usesOverflowActionsMenu) {
    setIsCompactActionsMenuOpen(false);
  }
}, [usesOverflowActionsMenu]);

useEffect(() => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

  const mediaQuery = window.matchMedia('(max-width: 767px)');
  const handleChange = (event) => {
    setIsMobileViewport(event.matches);
  };

  setIsMobileViewport(mediaQuery.matches);

  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }

  mediaQuery.addListener(handleChange);
  return () => mediaQuery.removeListener(handleChange);
}, []);

useEffect(() => {
  if (!isCompactActionsMenuOpen) return undefined;

  const handlePointerDown = (event) => {
    if (compactActionsMenuRef.current?.contains(event.target)) {
      return;
    }

    setIsCompactActionsMenuOpen(false);
  };

  document.addEventListener('mousedown', handlePointerDown);
  return () => document.removeEventListener('mousedown', handlePointerDown);
}, [isCompactActionsMenuOpen]);



  const findDatastreamMeta = (pin, dataId) => {
    const list = Array.isArray(datastreams) ? datastreams : [];
    if (!pin && !dataId) return null;
    if (pin) {
      const byPin = list.find(ds => String(ds?.pin || '') === String(pin));
      if (byPin) return byPin;
    }
    if (dataId != null) {
      const byId = list.find(ds => Number(ds?.id) === Number(dataId));
      if (byId) return byId;
    }
    return null;
  };

  const datastreamMeta = findDatastreamMeta(dataPin, dataId);
  const shouldPreferEditorSliderValue =
    type === 'slider' && typeof onWidgetPropsChange === 'function';

  const resolvedExtraProps = (() => {
    const base = cloneWidgetValue(extraProps || {});
    if (typeof base.showTitle !== 'boolean') {
      base.showTitle = true;
    }

    if (datastreamMeta) {
      if (!hasMeaningfulDatastreamValue(base.min) && datastreamMeta.min !== undefined && datastreamMeta.min !== null) {
        base.min = datastreamMeta.min;
      }
      if (!hasMeaningfulDatastreamValue(base.max) && datastreamMeta.max !== undefined && datastreamMeta.max !== null) {
        base.max = datastreamMeta.max;
      }
      if (
        !hasMeaningfulDatastreamValue(base.unit) &&
        datastreamMeta.unit !== undefined &&
        datastreamMeta.unit !== null &&
        String(datastreamMeta.unit).trim() !== ''
      ) {
        base.unit = datastreamMeta.unit;
      }
      if (type !== 'terminal') {
        const displayValue =
          shouldPreferEditorSliderValue && hasMeaningfulDatastreamValue(base.value)
            ? base.value
            : getDatastreamDisplayValue(datastreamMeta, base.value);
        if (displayValue !== undefined) {
          base.value = displayValue;
        }
      }
      const colorFallback = getDatastreamColorFallback(type, base, datastreamMeta);
      if (colorFallback) base.color = colorFallback;
    }

    const factoredBase = applyDatastreamFactorToProps(type, base, datastreamMeta);
    factoredBase.value = clampValueToBounds(factoredBase.value, factoredBase.min, factoredBase.max);

    return factoredBase;
  })();

  const themeExtraProps = useMemo(
    () => resolveThemeColors(resolvedExtraProps, mode),
    [resolvedExtraProps, mode]
  );

  const textTitleValue =
    type === 'text-title'
      ? (resolvedExtraProps?.title ?? title)
      : title;

  const renderedWidgetTitle = useMemo(() => {
    if (themeExtraProps?.showTitle === false) {
      return '';
    }

    const sourceTitle = type === 'text-title'
      ? textTitleValue
      : title;

    return sourceTitle == null ? '' : String(sourceTitle);
  }, [textTitleValue, themeExtraProps?.showTitle, title, type]);

  const responsiveWidgetVars = useMemo(
    () => getResponsiveWidgetVars(dims),
    [dims]
  );
  const labelFontSizeCss =
    toWidgetFontSizeCssValue(themeExtraProps?.labelFontSize) ||
    `${DEFAULT_WIDGET_LABEL_FONT_SIZE}px`;
  const valueFontSizeCss =
    toWidgetFontSizeCssValue(themeExtraProps?.valueFontSize) ||
    `${DEFAULT_WIDGET_VALUE_FONT_SIZE}px`;
  const chartExtraProps = {
    ...themeExtraProps,
    labelFontSize: themeExtraProps?.labelFontSize ?? DEFAULT_WIDGET_LABEL_FONT_SIZE,
    valueFontSize: themeExtraProps?.valueFontSize ?? DEFAULT_WIDGET_VALUE_FONT_SIZE,
  };
  const customFontSizeVars = {
    ...(labelFontSizeCss ? { '--widget-label-font-size': labelFontSizeCss } : {}),
    ...(valueFontSizeCss ? { '--widget-value-font-size': valueFontSizeCss } : {}),
  };
  const widgetClassName = [
    'widget',
    transparentBackground ? 'widget--transparent' : '',
    selected ? 'widget--selected' : '',
    isCompactActionsMenuOpen ? 'widget--actions-open' : '',
    'widget--custom-label-size',
    'widget--custom-value-size',
  ].filter(Boolean).join(' ');

  const responsiveWidgetTitleVars = useMemo(() => {
    const titleText = String(renderedWidgetTitle || '').trim();
    if (!titleText) {
      return null;
    }

    const baseWidth = Math.max(
      0,
      useRenderRect ? (renderWidthPx ?? 0) : (Number(dims?.width) || 0)
    );
    const baseHeight = Math.max(
      0,
      useRenderRect ? (renderHeightPx ?? 0) : (Number(dims?.height) || 0)
    );

    if (!baseWidth || !baseHeight) {
      return null;
    }

    const fontSizeMultiplier = getFontSizeMultiplier();
    const usableWidth = Math.max(baseWidth - 24, 36);
    const usableHeight = Math.max(baseHeight - 20, 24);
    const normalizedTitle = titleText.replace(/\s+/g, ' ');
    const words = normalizedTitle.split(' ').filter(Boolean);
    const longestWord = words.reduce(
      (currentLongest, word) => (word.length > currentLongest.length ? word : currentLongest),
      normalizedTitle
    );
    const desiredLines = baseWidth < 110 ? 4 : baseWidth < 170 ? 3 : baseWidth < 260 ? 2.5 : 2;
    const baseFontSize = clampNumber(
      Math.min(baseHeight * 0.26, baseWidth * 0.2, 56 * fontSizeMultiplier),
      14 * fontSizeMultiplier,
      56 * fontSizeMultiplier
    );
    const totalTextWidth = measureTextWidth(normalizedTitle, baseFontSize, 600);
    const longestWordWidth = measureTextWidth(longestWord, baseFontSize, 600);
    const widthScale = totalTextWidth > 0
      ? (usableWidth * desiredLines) / totalTextWidth
      : 1;
    const longestWordScale = longestWordWidth > 0
      ? (usableWidth * 0.94) / longestWordWidth
      : 1;
    const estimatedLineCount = totalTextWidth > 0
      ? clampNumber(Math.ceil(totalTextWidth / usableWidth), 1, Math.ceil(desiredLines))
      : 1;
    const heightScale = (usableHeight * 0.38)
      / Math.max(baseFontSize * 1.15 * estimatedLineCount, 1);
    const fittedFontSize = clampNumber(
      baseFontSize * Math.min(1, widthScale, longestWordScale, heightScale),
      8 * fontSizeMultiplier,
      72 * fontSizeMultiplier
    );

    return {
      '--widget-responsive-title-fit-size': `${Math.round(fittedFontSize * 10) / 10}px`,
    };
  }, [
    dims?.height,
    dims?.width,
    renderHeightPx,
    renderWidthPx,
    renderedWidgetTitle,
    useRenderRect,
  ]);

  const autoWidgetSizeStyle = useMemo(() => {
    if (isBaseWidget || !['gauge', 'radial-gauge'].includes(type)) {
      return null;
    }

    const baseWidth = Math.max(
      0,
      useRenderRect ? (renderWidthPx ?? 0) : (Number(dims?.width) || 0)
    );
    const baseHeight = Math.max(
      0,
      useRenderRect ? (renderHeightPx ?? 0) : (Number(dims?.height) || 0)
    );

    if (!baseWidth || !baseHeight) {
      return null;
    }

    const fontSizeMultiplier = getFontSizeMultiplier();
    const displayText = formatGaugeDisplayValue({
      value: themeExtraProps?.value,
      unit: themeExtraProps?.unit,
      showUnit: themeExtraProps?.showUnit !== false,
      decimals,
      separator: type === 'radial-gauge' ? ' ' : '',
    });

    if (!displayText || displayText === '—') {
      return null;
    }

    let valueFontSize = 0;
    let widthRatio = 0.8;
    let horizontalPadding = 0;

    if (type === 'gauge') {
      const availableHeight = Math.max(baseHeight - 60 * fontSizeMultiplier, 100);
      const availableWidth = Math.max(baseWidth - 20 * fontSizeMultiplier, 100);
      const radius = Math.min(availableHeight, availableWidth) / 2;
      valueFontSize = Math.max(radius * 0.25, 22 * fontSizeMultiplier);
      widthRatio = 0.78;
      horizontalPadding = 30 * fontSizeMultiplier;
    } else {
      const availableHeight = Math.max(baseHeight - 40 * fontSizeMultiplier, 50);
      const availableWidth = Math.max(baseWidth, 50);
      const radius = Math.min(availableHeight, availableWidth) / 2;
      valueFontSize = Math.max(radius * 0.18, 10 * fontSizeMultiplier);
      widthRatio = 0.72;
      horizontalPadding = 28 * fontSizeMultiplier;
    }

    const requiredContentWidth = measureTextWidth(displayText, valueFontSize, type === 'gauge' ? 700 : 600)
      + horizontalPadding;
    const requiredWidth = Math.ceil(requiredContentWidth / widthRatio);

    if (requiredWidth <= baseWidth + 4) {
      return null;
    }

    return {
      minWidth: `${requiredWidth}px`,
    };
  }, [
    decimals,
    dims?.height,
    dims?.width,
    isBaseWidget,
    renderHeightPx,
    renderWidthPx,
    themeExtraProps?.showUnit,
    themeExtraProps?.unit,
    themeExtraProps?.value,
    type,
    useRenderRect,
  ]);

  const strippedExtraPropsForEdit = (() => {
    const base = cloneWidgetValue(settingsExtraProps ?? extraProps ?? {});
    const colorFallback = getDatastreamColorFallback(type, base, datastreamMeta);
    if (colorFallback) base.color = colorFallback;
    if (datastreamMeta) {
      delete base.min;
      delete base.max;
      delete base.unit;
    }
    if (FACTORED_WIDGET_TYPES.has(type) && !Number.isFinite(Number(base.factor))) {
      base.factor = 1;
    }
    if (type === 'chart' && typeof base.showUnit !== 'boolean') {
      base.showUnit = true;
    }
    return base;
  })();

  const stopControlPointerEvent = (e) => {
    e.stopPropagation();
  };

  const preventControlDragEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDuplicateClick = (e) => {
    stopControlPointerEvent(e);
    setIsCompactActionsMenuOpen(false);
    duplicate(id);
  };

  const handleSettingsClick = (e) => {
    stopControlPointerEvent(e);
    setIsCompactActionsMenuOpen(false);
    if (setEdit) {
      const geometryWidth = useRenderRect ? renderWidthPx : toLengthNumber(width, 0);
      const geometryHeight = useRenderRect ? renderHeightPx : toLengthNumber(height, 0);
      const geometryTop = useRenderRect ? renderTopPx : toLengthNumber(top, 0);
      const geometryLeft = useRenderRect ? renderLeftPx : toLengthNumber(left, 0);
      setEdit({
        open: true,
        id: id,
        dataId: dataId,
        dataPin: dataPin,
        deviceId: deviceId,
        type: type,
        title: title,
        transparentBackground: transparentBackground,
        width: geometryWidth,
        height: geometryHeight,
        top: geometryTop,
        left: geometryLeft,
        props: strippedExtraPropsForEdit,
        showCurves: extraProps?.curves ? extraProps.curves.map(() => false) : []
      });
    }
    if (setDeviceDatastream && deviceId) {
      setDeviceDatastream(devices.find(device => device.id === deviceId)?.template?.datastreams);
    }
  };

  const handleRemoveClick = (e) => {
    stopControlPointerEvent(e);
    setIsCompactActionsMenuOpen(false);
    remove(id);
  };



  return (
    <div id={id} dir={isRtl ? 'rtl' : 'ltr'} className={widgetClassName} style={{
      position: position,
      width: useRenderRect
        ? `${renderWidthPx}px`
        : (isPercentageLayout ? toCssLength(width, '0%') : `${dims.width}px`),
      height: useRenderRect
        ? `${renderHeightPx}px`
        : (isPercentageLayout ? toCssLength(height, '0%') : `${dims.height}px`),
      top: useRenderRect
        ? `${renderTopPx}px`
        : (isPercentageLayout ? toCssLength(top, '0%') : top),
      left: useRenderRect
        ? `${renderLeftPx}px`
        : (isPercentageLayout ? toCssLength(left, '0%') : left),
      transform: dragOffset
        ? `translate(${dragOffset.x || 0}px, ${dragOffset.y || 0}px)`
        : undefined,
      transition: dragOffset ? 'none' : undefined,
      direction: isRtl ? 'rtl' : 'ltr',
      ...responsiveWidgetVars,
      ...customFontSizeVars,
      ...(responsiveWidgetTitleVars || {}),
      ...(autoWidgetSizeStyle || {}),
    }}>
      {type === 'chart' ? <Chart key={`${id}-${Math.round(dims.width)}-${Math.round(dims.height)}`} title={renderedWidgetTitle} transparentBackground={transparentBackground} pin={dataPin} {...chartExtraProps} draggable={draggable} id={id} dims={dims} datastreams={datastreams} /> :
        type === 'period-column' ? <PeriodColumnChart key={`${id}-${Math.round(dims.width)}-${Math.round(dims.height)}`} title={renderedWidgetTitle} transparentBackground={transparentBackground} pin={dataPin} {...chartExtraProps} draggable={draggable} id={id} dims={dims} datastreams={datastreams} decimals={decimals} /> :
        type === 'gauge' ? <Gauge id={id} {...themeExtraProps} pin={dataPin} decimals={decimals} draggable={draggable} dims={dims} title={renderedWidgetTitle} transparentBackground={transparentBackground} datastreams={datastreams} /> :
          type === 'liquid' ? <LiquidWidget id={id} {...themeExtraProps} decimals={decimals} draggable={draggable} dims={dims} title={renderedWidgetTitle} transparentBackground={transparentBackground} /> :
            type === 'water-tank' ? <WaterTankWidget id={id} {...themeExtraProps} decimals={decimals} draggable={draggable} dims={dims} title={renderedWidgetTitle} transparentBackground={transparentBackground} /> :
          type === 'radial-gauge' ? <RadialGauge id={id} {...themeExtraProps} pin={dataPin} decimals={decimals} draggable={draggable} dims={dims} title={renderedWidgetTitle} transparentBackground={transparentBackground} datastreams={datastreams} /> :
            type === 'label' ? <Label id={id} title={renderedWidgetTitle} transparentBackground={transparentBackground} decimals={decimals} {...themeExtraProps} pin={dataPin} draggable={draggable} /> :
              type === 'led' ? <LED  {...themeExtraProps} pin={dataPin} draggable={draggable} title={renderedWidgetTitle} transparentBackground={transparentBackground} /> :
                type === 'number' ? <NumberInput did={did} tid={tid} datastreamColor={datastreamMeta?.color} {...themeExtraProps} pin={dataPin} draggable={draggable} title={renderedWidgetTitle} transparentBackground={transparentBackground} /> :
                    type === 'slider' ? <Slider did={did} tid={tid} datastreamColor={datastreamMeta?.color} title={renderedWidgetTitle} transparentBackground={transparentBackground} {...themeExtraProps} pin={dataPin} draggable={draggable} onValueCommit={(nextValue) => onWidgetPropsChange?.({ value: nextValue })} /> :
                      type === 'switch' ? <Switch did={did} tid={tid} title={renderedWidgetTitle} transparentBackground={transparentBackground} {...themeExtraProps} pin={dataPin} draggable={draggable} /> :
                        type === 'push-button' ? <PushButton did={did} tid={tid} title={renderedWidgetTitle} transparentBackground={transparentBackground} {...themeExtraProps} pin={dataPin} draggable={draggable} /> :
                        type === 'terminal' ? <Terminal did={did} tid={tid} id={id} title={renderedWidgetTitle} transparentBackground={transparentBackground} {...themeExtraProps} pin={dataPin} dataId={dataId} deviceId={deviceId} onTerminalCleared={onTerminalCleared} draggable={draggable} /> :
                        type === 'text' ? <TextInput did={did} tid={tid} title={renderedWidgetTitle} transparentBackground={transparentBackground} decimals={decimals} {...themeExtraProps} pin={dataPin} draggable={draggable} /> :
                        type === 'text-title' ? <TextTitle did={did} tid={tid} title={renderedWidgetTitle} transparentBackground={transparentBackground} decimals={decimals} {...themeExtraProps} pin={dataPin} draggable={draggable} /> :
                          type === 'menu' ? <Menu did={did} tid={tid} title={renderedWidgetTitle} transparentBackground={transparentBackground} {...themeExtraProps} pin={dataPin} draggable={draggable} /> :
                            type === 'segment-switch' ? <SegmentSwitch did={did} tid={tid} title={renderedWidgetTitle} transparentBackground={transparentBackground} {...themeExtraProps} pin={dataPin} draggable={draggable} /> :
                              type === 'background-image' ? <BackgroundImage  {...themeExtraProps} pin={dataPin} draggable={draggable} title={renderedWidgetTitle} /> :
                                type === 'box-widget' ? <BoxWidget id={id} title={renderedWidgetTitle} draggable={draggable} {...themeExtraProps} /> :
                                type === 'image-button' ? <ImageButton did={did} tid={tid} title={renderedWidgetTitle} transparentBackground={transparentBackground} {...themeExtraProps} pin={dataPin} draggable={draggable} /> :
                                  type === 'keypad' ? <Keypad did={did} tid={tid} title={renderedWidgetTitle} transparentBackground={transparentBackground} {...themeExtraProps} pin={dataPin} draggable={draggable} /> :
                                    type === 'table' ?
                                      <TableWidget
                                        title={renderedWidgetTitle}
                                        transparentBackground={transparentBackground}
                                        draggable={draggable}
                                        dims={dims}
                                        xData={themeExtraProps.xData}
                                        curves={themeExtraProps.curves}
                                        headerBgColor={themeExtraProps.headerBgColor}
                                        headerTextColor={themeExtraProps.headerTextColor}
                                        bodyBgColor={themeExtraProps.bodyBgColor}
                                        bodyTextColor={themeExtraProps.bodyTextColor}
                                      /> :
                                      type === 'fan' ? (
                                        <Fan
                                          did={did}
                                          tid={tid}
                                          title={renderedWidgetTitle}
                                          transparentBackground={transparentBackground}
                                          {...themeExtraProps}
                                          draggable={draggable}
                                          dims={dims}
                                        />
                                        ) :
                                        type === 'time-range' ? (
                                          <TimeRange
                                            title={renderedWidgetTitle}
                                            transparentBackground={transparentBackground}
                                            {...themeExtraProps}
                                            draggable={draggable}
                                            dims={dims}
                                            datastreams={datastreams}
                                            devices={devices}
                                            did={did}
                                            tid={tid}
                                          />
                                        ) :
                                        type === 'environmental-status' ? (
                                          <EnvironmentalStatus
                                            id={id}
                                            title={renderedWidgetTitle}
                                            transparentBackground={transparentBackground}
                                            {...themeExtraProps}
                                            draggable={draggable}
                                            dims={dims}
                                            datastreams={datastreams}
                                            devices={devices}
                                          />
                                        ) :
                                        type === 'pipe' ? (
                                          <WaterPipe
                                            did={did}
                                            tid={tid}
                                            title={renderedWidgetTitle}
                                            transparentBackground={transparentBackground}
                                            {...themeExtraProps}
                                            pin={dataPin}
                                            draggable={draggable}
                                            dims={dims}
                                          />
                                        ) :
                                          type === 'water-pump' ? (
                                            <WaterPump
                                              did={did}
                                              tid={tid}
                                              title={renderedWidgetTitle}
                                              transparentBackground={transparentBackground}
                                              {...themeExtraProps}
                                              pin={dataPin}
                                              draggable={draggable}
                                              dims={dims}
                                            />
                                          ) :
                                          type === 'elbow' ? (
                                            <WaterElbow
                                              did={did}
                                              tid={tid}
                                              title={renderedWidgetTitle}
                                              transparentBackground={transparentBackground}
                                              {...themeExtraProps}
                                              pin={dataPin}
                                              draggable={draggable}
                                              dims={dims}
                                            />
                                          )
                                            : null}
      {resize ? <Fragment>
        <div className='widget-control-panel'></div>
        <div
          className='widget-buttons-container'
          onMouseDown={stopControlPointerEvent}
          onMouseUp={stopControlPointerEvent}
          onClick={stopControlPointerEvent}
        >
          {usesOverflowActionsMenu ? (
            <div
              className='widget-actions-menu-wrap'
              ref={compactActionsMenuRef}
              onMouseDown={stopControlPointerEvent}
              onClick={stopControlPointerEvent}
            >
              <button
                type='button'
                draggable={false}
                onMouseDown={stopControlPointerEvent}
                onDragStart={preventControlDragEvent}
                onClick={(e) => {
                  stopControlPointerEvent(e);
                  setIsCompactActionsMenuOpen((prev) => !prev);
                }}
                className='widget-btn widget-btn-menu'
              >
                <MoreVertIcon fontSize='small' />
              </button>

              {isCompactActionsMenuOpen ? (
                <div className='widget-actions-menu'>
                  <button
                    type='button'
                    className='widget-actions-menu-item'
                    onMouseDown={stopControlPointerEvent}
                    onClick={handleDuplicateClick}
                  >
                    <WidgetDuplicateIcon />
                    <span>Duplicate</span>
                  </button>

                  <button
                    type='button'
                    className='widget-actions-menu-item'
                    onMouseDown={stopControlPointerEvent}
                    onClick={handleSettingsClick}
                  >
                    <WidgetSettingsIcon />
                    <span>Settings</span>
                  </button>

                  <button
                    type='button'
                    className='widget-actions-menu-item widget-actions-menu-item--danger'
                    onMouseDown={stopControlPointerEvent}
                    onClick={handleRemoveClick}
                  >
                    <WidgetDeleteIcon color={'var(--root-background)'} />
                    <span>Delete</span>
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <Fragment>
              <button
                type='button'
                draggable={false}
                onMouseDown={stopControlPointerEvent}
                onDragStart={preventControlDragEvent}
                onClick={handleDuplicateClick}
                className='widget-btn'
              >
                <WidgetDuplicateIcon />
              </button>
              <button
                type='button'
                draggable={false}
                onMouseDown={stopControlPointerEvent}
                onDragStart={preventControlDragEvent}
                onClick={handleSettingsClick}
                className='widget-btn'
              >
                <WidgetSettingsIcon />
              </button>
              <button
                type='button'
                draggable={false}
                onMouseDown={stopControlPointerEvent}
                onDragStart={preventControlDragEvent}
                onClick={handleRemoveClick}
                className='widget-btn widget-btn-remove'
              >
                <WidgetDeleteIcon color={'var(--root-background)'} />
              </button>
            </Fragment>
          )}
        </div>
        <div
          onMouseDown={(e) => resizeWidget(e, id, setDims)}
        >
          <div className='widget-resizer'></div>
          <div className='second-widget-resizer'></div>
        </div>

      </Fragment> : null}
    </div>
  );
};

export default Widget;
